/**
 * Sentinella dei blocchi dell'aula Jitsi — gemella di
 * assets/js/sentinella-aula.js della piattaforma (STESSA logica: se si cambia
 * una, si cambia anche l'altra).
 *
 * SOLO rileva e segnala: non riapre l'aula, non mostra nulla all'utente e non
 * fa richieste al server finche' non c'e' un blocco.
 *
 * Ogni 5 s chiede all'iframe di Jitsi `isAudioMuted()` (postMessage interno al
 * browser, torna con una Promise). Se per 3 controlli di fila la risposta non
 * arriva (~15 s) l'aula e' BLOCCATA → una segnalazione; quando torna a
 * rispondere → «sbloccata dopo N secondi». Una sola domanda in volo per volta.
 * Prima che l'iframe sia caricato i controlli non contano, tranne dopo 45 s.
 *
 * Scatola nera: lo script dentro la pagina di Jitsi (scatola-nera-aula.js sul
 * server dell'aula) dopo un blocco manda con postMessage quello che ha
 * annotato da dentro. Si accetta solo dall'origine del server dell'aula e
 * dall'iframe di questa api, e si inoltra come segnalazione «scatola».
 *
 * Caduta: se Jitsi da' un errore (`errorOccurred`, per esempio
 * connection-dropped-error quando il server ha chiuso il collegamento) o il
 * browser resta senza rete per almeno 10 s, una segnalazione «caduta». Parte
 * quando la rete c'e' (dopo 3 s se c'e' gia', altrimenti al ritorno online),
 * al massimo una al minuto.
 *
 * Rete: ogni segnalazione porta com'era la rete secondo il browser
 * (navigator.connection: tipo, velocita' e ritardo stimati; non c'e' su
 * Safari e Firefox) e le ultime volte che e' andato offline, con i secondi.
 *
 * Pagina: le ultime volte che la pagina e' rimasta nascosta (schermo spento,
 * altra app o altra scheda: `visibilitychange`) e congelata dal browser
 * (`freeze`/`resume`, solo Chrome), con i secondi, e se e' nascosta adesso.
 */

const INTERVALLO_MS = 5000;
const SOGLIA = 3;
const GRAZIA_MS = 45000;
const MAX_EVENTI = 10;
const MAX_RIGHE = 30, MAX_ERRORI = 10, MAX_LOAF = 5, MAX_SCRIPT = 4, LUNG = 150;
const TETTO_BYTE = 15500;     // l'endpoint rifiuta oltre i 16 KB
const MAX_RETE = 5;           // ultime volte offline / cambi di rete tenuti
const OFFLINE_MIN_MS = 10000; // offline almeno 10 s = caduta
const CADUTA_OGNI_MS = 60000; // al massimo una caduta al minuto
const CADUTA_ATTESA_MS = 3000;
const MAX_PAGINA = 5;         // ultime volte nascosta / congelata tenute

const EVENTI = ['browserSupport', 'cameraError', 'micError', 'videoConferenceJoined',
    'videoConferenceLeft', 'audioMuteStatusChanged', 'videoMuteStatusChanged',
    'errorOccurred', 'readyToClose'];

export interface SentinellaAulaConfig {
    url: string;
    ruolo: 'docente' | 'tutor' | 'corsista';
    dominio: string;      // "dad.discentya.cloud/<tenant>/"
    stanza: string;
    idUtente: string | number;
    nome?: string;
}

function due(n: number): string { return (n < 10 ? '0' : '') + n; }

function oraBreve(d: Date): string {
    return due(d.getHours()) + ':' + due(d.getMinutes()) + ':' + due(d.getSeconds());
}

function oraLocale(d: Date): string {
    let off = -d.getTimezoneOffset();
    const segno = off >= 0 ? '+' : '-';
    off = Math.abs(off);
    return d.getFullYear() + '-' + due(d.getMonth() + 1) + '-' + due(d.getDate()) +
        'T' + oraBreve(d) + segno + due(Math.floor(off / 60)) + ':' + due(off % 60);
}

function idCasuale(): string {
    let s = '';
    while (s.length < 12) s += Math.random().toString(36).slice(2);
    return s.slice(0, 12);
}

function messaggio(ev: any): string {
    if (!ev) return '';
    let m = ev.message || (ev.error && ev.error.message) || ev.name || ev.type || '';
    if (ev.type && ev.message && ev.type !== ev.message) m = ev.type + ' ' + ev.message;
    return String(m).replace(/[^\x20-\x7E\u00C0-\u017F]/g, ' ').slice(0, 150);
}

// Le stringhe di una lista della scatola: solo testo fino a LUNG caratteri,
// le altre si scartano; della lista si tengono le ultime `max`.
function stringhe(lista: any, max: number): string[] {
    if (!Array.isArray(lista)) return [];
    return lista.filter((x: any) => typeof x === 'string' && x !== '' && x.length <= LUNG).slice(-max);
}

function conn(): any {
    const n: any = window.navigator || {};
    return n.connection || n.mozConnection || n.webkitConnection || null;
}

// Un errore di Jitsi in breve: l'evento e' { error: { name, message, type, isFatal } }
function erroreDa(ev: any): any {
    const e: any = (ev && ev.error && typeof ev.error === 'object') ? ev.error : (ev || {});
    const pulito = (v: any, max: number, re: RegExp) => { v = typeof v === 'string' ? v.slice(0, max) : ''; return re.test(v) ? v : null; };
    return {
        tipo: pulito(e.type, 20, /^[A-Z_]+$/),
        nome: pulito(e.name, 100, /^[a-zA-Z0-9._\-]+$/),
        msg: messaggio(ev) || null,
        fatale: typeof e.isFatal === 'boolean' ? e.isFatal : null
    };
}

// Dai dati della scatola nera solo quello che va nel registro
function scatolaDa(tipo: string, d: any): any {
    const loaf: any[] = Array.isArray(d.loaf) ? d.loaf : [];
    return {
        origine: tipo,
        secBlocco: typeof d.secBlocco === 'number' ? Math.round(d.secBlocco) : null,
        entrato: d.entrato === true,
        righe: stringhe(d.righe, MAX_RIGHE),
        errori: stringhe(d.errori, MAX_ERRORI),
        loaf: loaf.filter(v => v && typeof v === 'object').slice(-MAX_LOAF).map(v => ({
            t: typeof v.t === 'string' ? v.t : null, ms: typeof v.ms === 'number' ? Math.round(v.ms) : null,
            script: stringhe(v.script, MAX_SCRIPT)
        }))
    };
}

export class SentinellaAula {
    private dati: any;
    private t0 = Date.now();
    private eventi: any[] = [];
    private passo = '';
    private caricato = false;
    private vivo = false;
    private inVolo: number = null;
    private giro = 0;
    private mancate = 0;
    private blocco: { id: string, inizio: number } = null;
    private fermo = false;
    private ascolti: [string, (ev: any) => void][] = [];
    private timer: any;
    private fPagina = () => this.ferma();
    private fMessaggio = (ev: MessageEvent) => this.scatola(ev);
    private giu: { t: string, sec: number, da: number }[] = [];   // le ultime volte offline
    private cambi: any[] = [];                                     // { t, efficace, mbps, rttMs } i cambi di rete
    private ultimaCaduta = 0;
    private cadutaAttesa: { motivo: string, errore: any } = null; // da mandare quando c'e' rete
    private fOffline = () => this.offline();
    private fOnline = () => this.online();
    private fCambio = () => this.cambio();
    private nascosta: { t: string, sec: number, da: number }[] = [];  // le ultime volte che la pagina era nascosta
    private congelata: { t: string, sec: number, da: number }[] = []; // le ultime volte che il browser l'ha congelata
    private fVisibile = () => this.segna(this.nascosta, document.hidden);
    private fFreeze = () => this.segna(this.congelata, true);
    private fResume = () => this.segna(this.congelata, false);

    constructor(private api: any, private url: string, cfg: SentinellaAulaConfig) {
        const parti = String(cfg.dominio || '').split('/').filter(Boolean);
        this.dati = {
            server: parti[0] || '',
            tenant: parti[1] || '',
            stanza: String(cfg.stanza || ''),
            ruolo: cfg.ruolo,
            idUtente: parseInt(String(cfg.idUtente), 10) || 0,
            nome: String(cfg.nome || '').replace(/[^\p{L}\p{N} _.,:;'()\/\-+=#@!?%&\[\]]/gu, ' ').trim().slice(0, 120)
        };
        if (document.hidden) this.nascosta.push({ t: oraBreve(new Date()), sec: null, da: Date.now() });

        this.nota('api creata');

        EVENTI.forEach(nome => {
            const f = (ev: any) => {
                this.nota(nome, (nome === 'errorOccurred' || nome === 'cameraError' || nome === 'micError') ? messaggio(ev) : '');
                if (nome === 'errorOccurred') this.caduta('errore', erroreDa(ev));
                if (nome === 'readyToClose') this.ferma();
            };
            this.ascolti.push([nome, f]);
            try { api.addListener(nome, f); } catch (e) {}
        });
        // dispose(): l'api emette `_willDispose` prima di smontare l'iframe
        const fDispose = () => this.ferma();
        this.ascolti.push(['_willDispose', fDispose]);
        try { api.addListener('_willDispose', fDispose); } catch (e) {}

        const iframe = this.iframe();
        if (iframe) {
            iframe.addEventListener('load', () => {
                if (!this.caricato) { this.caricato = true; this.nota('iframe caricato'); }
            });
        }

        window.addEventListener('pagehide', this.fPagina);
        window.addEventListener('message', this.fMessaggio);
        window.addEventListener('offline', this.fOffline);
        window.addEventListener('online', this.fOnline);
        const c = conn();
        if (c && c.addEventListener) { try { c.addEventListener('change', this.fCambio); } catch (e) {} }
        document.addEventListener('visibilitychange', this.fVisibile);
        document.addEventListener('freeze', this.fFreeze);
        document.addEventListener('resume', this.fResume);

        this.timer = setInterval(() => this.controlla(), INTERVALLO_MS);
        this.controlla();
    }

    private iframe(): HTMLIFrameElement {
        try { return this.api.getIFrame ? this.api.getIFrame() : null; } catch (e) { return null; }
    }

    private nota(nome: string, msg: string = ''): void {
        this.passo = msg ? nome + ': ' + msg : nome;
        const ev: any = { t: oraBreve(new Date()), e: nome };
        if (msg) ev.m = msg;
        this.eventi.push(ev);
        if (this.eventi.length > MAX_EVENTI) this.eventi.shift();
    }

    private controlla(): void {
        if (this.fermo) return;
        const iframe: any = this.iframe();
        // iframe tolto dalla pagina senza dispose(): non c'e' piu' niente da controllare
        if (!iframe || !iframe.isConnected) { this.ferma(); return; }

        if (this.inVolo !== null) {
            const conta = this.vivo || this.caricato || (Date.now() - this.t0 >= GRAZIA_MS);
            if (!conta) return;
            this.mancate++;
            if (this.mancate >= SOGLIA && !this.blocco) {
                this.blocco = { id: idCasuale(), inizio: this.inVolo };
                this.invia('bloccata', this.blocco.id, {});
            }
            return;
        }

        const giro = ++this.giro;
        this.inVolo = Date.now();
        const risposta = () => {
            if (this.fermo || giro !== this.giro) return;
            this.inVolo = null;
            this.mancate = 0;
            this.vivo = true;
            if (this.blocco) {
                this.invia('sbloccata', this.blocco.id, { secBlocco: Math.round((Date.now() - this.blocco.inizio) / 1000) });
                this.blocco = null;
            }
        };
        try {
            this.api.isAudioMuted().then(risposta, risposta);
        } catch (e) {
            this.inVolo = null;
        }
    }

    // Messaggio della scatola nera: solo dal server dell'aula e dall'iframe di questa api
    private scatola(ev: MessageEvent): void {
        if (this.fermo || !ev || !this.dati.server) return;
        const iframe = this.iframe();
        if (ev.origin !== 'https://' + this.dati.server) return;
        if (!iframe || ev.source !== iframe.contentWindow) return;
        const m: any = ev.data;
        if (!m || typeof m !== 'object' || m.scatolaNeraAula !== 1) return;
        if ((m.tipo !== 'blocco' && m.tipo !== 'precedente') || !m.dati || typeof m.dati !== 'object') return;
        this.invia('scatola', idCasuale(), { scatola: scatolaDa(m.tipo, m.dati) });
    }

    // La rete adesso, secondo il browser
    private rete(): any {
        const n: any = window.navigator || {};
        const c: any = conn() || {};
        return {
            online: n.onLine !== false,
            tipo: typeof c.type === 'string' ? c.type : null,
            efficace: typeof c.effectiveType === 'string' ? c.effectiveType : null,
            mbps: typeof c.downlink === 'number' ? c.downlink : null,
            rttMs: typeof c.rtt === 'number' ? c.rtt : null,
            risparmio: typeof c.saveData === 'boolean' ? c.saveData : null,
            giu: this.giu.map(g => ({ t: g.t, sec: g.sec })),
            cambi: this.cambi.slice()
        };
    }

    private offline(): void {
        if (this.fermo) return;
        this.nota('offline');
        this.giu.push({ t: oraBreve(new Date()), sec: null, da: Date.now() });
        if (this.giu.length > MAX_RETE) this.giu.shift();
    }

    private online(): void {
        if (this.fermo) return;
        const g = this.giu[this.giu.length - 1];
        let sec: number = null;
        if (g && g.sec === null) { g.sec = Math.round((Date.now() - g.da) / 1000); sec = g.sec; }
        this.nota('online', sec !== null ? 'dopo ' + sec + ' s' : '');
        if (this.cadutaAttesa) this.mandaCaduta();
        else if (sec !== null && sec * 1000 >= OFFLINE_MIN_MS) this.caduta('offline', null);
    }

    // Inizio (inizio=true) o fine di un periodo nascosta / congelata
    private segna(lista: { t: string, sec: number, da: number }[], inizio: boolean): void {
        if (this.fermo) return;
        const u = lista[lista.length - 1];
        if (inizio) {
            if (u && u.sec === null) return;
            lista.push({ t: oraBreve(new Date()), sec: null, da: Date.now() });
            if (lista.length > MAX_PAGINA) lista.shift();
        } else if (u && u.sec === null) {
            u.sec = Math.round((Date.now() - u.da) / 1000);
        }
    }

    private pagina(): any {
        const solo = (l: { t: string, sec: number }[]) => l.map(g => ({ t: g.t, sec: g.sec }));
        return { nascostaOra: document.hidden === true, nascosta: solo(this.nascosta), congelata: solo(this.congelata) };
    }

    private cambio(): void {
        if (this.fermo) return;
        const c: any = conn() || {};
        this.cambi.push({ t: oraBreve(new Date()), efficace: typeof c.effectiveType === 'string' ? c.effectiveType : null,
            mbps: typeof c.downlink === 'number' ? c.downlink : null, rttMs: typeof c.rtt === 'number' ? c.rtt : null });
        if (this.cambi.length > MAX_RETE) this.cambi.shift();
    }

    // La caduta si manda quando la rete c'e': dopo 3 s se c'e' gia' (cosi'
    // prende anche l'«online» che arriva insieme), altrimenti al ritorno online
    private caduta(motivo: string, errore: any): void {
        if (this.fermo || this.cadutaAttesa || Date.now() - this.ultimaCaduta < CADUTA_OGNI_MS) return;
        this.cadutaAttesa = { motivo: motivo, errore: errore };
        const n: any = window.navigator || {};
        if (n.onLine !== false) setTimeout(() => this.mandaCaduta(), CADUTA_ATTESA_MS);
    }

    private mandaCaduta(): void {
        const a = this.cadutaAttesa;
        if (!a) return;
        const n: any = window.navigator || {};
        if (n.onLine === false) return;      // si riprova all'«online»
        this.cadutaAttesa = null;
        this.ultimaCaduta = Date.now();
        const aggiunte: any = { motivo: a.motivo };
        if (a.errore) aggiunte.errore = a.errore;
        this.invia('caduta', idCasuale(), aggiunte);
    }

    private invia(tipo: string, id: string, aggiunte: any): void {
        const n: any = window.navigator || {};
        const r: any = {
            tipo: tipo,
            id: id,
            ora: oraLocale(new Date()),
            server: this.dati.server,
            tenant: this.dati.tenant,
            stanza: this.dati.stanza,
            ruolo: this.dati.ruolo,
            idUtente: this.dati.idUtente,
            nome: this.dati.nome,
            ua: String(n.userAgent || '').slice(0, 400),
            cpu: n.hardwareConcurrency || null,
            ram: n.deviceMemory || null,
            secAperta: Math.round((Date.now() - this.t0) / 1000),
            passo: this.passo,
            eventi: this.eventi.slice(),
            rete: this.rete(),
            pagina: this.pagina()
        };
        Object.keys(aggiunte).forEach(k => r[k] = aggiunte[k]);
        try { console.warn('[sentinella-aula] ' + tipo + (tipo === 'sbloccata' ? ' dopo ' + r.secBlocco + ' s' : '')); } catch (e) {}

        // sotto i 16 KB: via prima le righe piu' vecchie, poi errori e loaf
        let json = JSON.stringify(r);
        const sc = r.scatola;
        while (sc && json.length > TETTO_BYTE && (sc.righe.length || sc.errori.length || sc.loaf.length)) {
            (sc.righe.length ? sc.righe : sc.errori.length ? sc.errori : sc.loaf).shift();
            json = JSON.stringify(r);
        }

        const corpo = new FormData();
        corpo.append('r', json);
        let partito = false;
        try { partito = n.sendBeacon ? n.sendBeacon(this.url, corpo) : false; } catch (e) {}
        if (!partito && (window as any).fetch) {
            try { fetch(this.url, { method: 'POST', body: corpo, keepalive: true, credentials: 'include' } as any).catch(() => {}); } catch (e) {}
        }
    }

    ferma(): void {
        if (this.fermo) return;
        this.fermo = true;
        clearInterval(this.timer);
        window.removeEventListener('pagehide', this.fPagina);
        window.removeEventListener('message', this.fMessaggio);
        window.removeEventListener('offline', this.fOffline);
        window.removeEventListener('online', this.fOnline);
        const c = conn();
        if (c && c.removeEventListener) { try { c.removeEventListener('change', this.fCambio); } catch (e) {} }
        document.removeEventListener('visibilitychange', this.fVisibile);
        document.removeEventListener('freeze', this.fFreeze);
        document.removeEventListener('resume', this.fResume);
        this.ascolti.forEach(a => {
            try { this.api.removeListener(a[0], a[1]); } catch (e) {}
        });
        if ((window as any).__sentinellaAula === this) (window as any).__sentinellaAula = null;
    }
}

/** Una sola sentinella per pagina: una nuova aula spegne quella di prima. */
export function agganciaSentinellaAula(api: any, cfg: SentinellaAulaConfig): SentinellaAula {
    const w: any = window;
    if (!api || !cfg || !cfg.url) return null;
    if (w.__sentinellaAula) w.__sentinellaAula.ferma();
    try {
        w.__sentinellaAula = new SentinellaAula(api, cfg.url, cfg);
    } catch (e) {
        w.__sentinellaAula = null;
    }
    return w.__sentinellaAula;
}
